// Schadenmappe (Stufe 3, Spec §1) — Controller der Seite
// `/schaeden/:id/:bereich?/:schritt?` und `/schaeden/neu`: Route auflösen,
// Schaden laden, Seitenleiste und Kopfzeile versorgen, den Schaden anlegen,
// sobald Kontakt und Schadenart feststehen, danach die Meldung automatisch
// speichern (Kontakt/Risiko/Vertrag und Abschluss sofort). Daneben die Bereiche
// „Bearbeitung“ und „Regulierung“.
import * as React from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import useResponsive from "../../hooks/useResponsive";
import { createDamage, getSchadenmappe, updateDamage } from "../../components/schadenmappe/api";
import type { MappeDamage, SchadenmappeData } from "../../components/schadenmappe/types";
import type { Speicherzustand } from "../../components/schadenmappe/Kopfzeile";
import { Seitenleiste } from "../../components/schadenmappe/Seitenleiste";
import {
  istBereich,
  pflichtErfuellt,
  schrittEintraege,
  schrittLabel,
  schrittListeIntern,
  startSchritt,
  type Bereich,
  type SchrittEintrag,
} from "../../components/schadenmappe/meldung/schritte";
import type { AbschlussWerte } from "../../components/schadenmappe/meldung/AbschlussIntern";
import {
  ABSCHLUSS_LEER,
  abschlussAusSchaden,
  abschlussGeaendert,
  abschlussPatchFuerServer,
} from "../../components/schadenmappe/meldung/abschlussPatch";
import {
  useMeldungAutosave,
  type AutosaveBasis,
} from "../../components/schadenmappe/meldung/useMeldungAutosave";
import { useSchadenAnlage } from "../../components/schadenmappe/meldung/useSchadenAnlage";
import { useSofortSpeichern } from "../../components/schadenmappe/meldung/useSofortSpeichern";
import { MeldungBereich } from "../../components/schadenmappe/meldung/MeldungBereich";
import { BearbeitungBereich } from "../../components/schadenmappe/bearbeiten/BearbeitungBereich";
import { RegulierungBereich } from "../../components/schadenmappe/regulierung/RegulierungBereich";
import { buildDescription } from "../../components/schadenmeldung-big/serialize";
import {
  initialBigAnswers,
  type BigAnswers,
  type BigStepKey,
} from "../../components/schadenmeldung-big/types";
import "../../components/redesign/contactRedesign.css";
import "../../components/schadenmappe/schadenmappe.css";

/** Ab dieser Breite wird die Seitenleiste zur aufklappbaren Zeile (wie die CSS-Media-Query). */
const TELEFON_BREITE = 840;

/** Vorauswahl aus `/schaeden/neu?contactId=…&riskObjectId=…&contractId=…`. */
export interface Vorauswahl {
  contactId: number | null;
  riskObjectId: number | null;
  contractId: number | null;
}

const zahlOderNull = (v: string | null): number | null => {
  if (v === null || v.trim() === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/** Leere Menge besuchter Schritte — stabil, damit die Einträge-Memo nicht kippt. */
const KEINE_BESUCHT: ReadonlySet<BigStepKey> = new Set();

/** `Damage.meldung` → Antworten des Wizards (über die Startwerte gelegt) + interne Schadensart. */
export function antwortenAusMeldung(meldung: Record<string, unknown> | null | undefined): {
  answers: BigAnswers;
  schadensart: string | null;
} {
  if (!meldung) return { answers: initialBigAnswers, schadensart: null };
  // `v` (Version), `schadensart` und `mappe` (Zusatzangaben der „Bearbeitung“,
  // eigener Speicherweg) gehören nicht zu den Antworten — sonst schriebe der
  // Meldungs-Autosave einen veralteten `mappe`-Stand zurück.
  const { v: _v, mappe: _mappe, schadensart, ...rest } = meldung;
  return {
    answers: { ...initialBigAnswers, ...(rest as Partial<BigAnswers>) },
    schadensart: typeof schadensart === "string" ? schadensart : null,
  };
}

interface Verweis {
  id: number;
  label: string;
}

/**
 * Lokaler Bearbeitungsstand der Meldung, an den Schaden gebunden, für den er
 * gilt (`null` = Neuanlage vor der Anlage). Aus dem Server wird nur gesät, wenn
 * der geladene Schaden ein anderer ist — sonst überschriebe ein Nachladen die
 * Eingaben.
 */
interface Bearbeitung {
  damageId: number | null;
  answers: BigAnswers;
  schadensart: string | null;
  kontakt: Verweis | null;
  risiko: Verweis | null;
  vertrag: Verweis | null;
  /** Meldung an den Versicherer (Spalten des Schadens). */
  abschluss: AbschlussWerte;
  /**
   * „heute“ wurde als Meldedatum schon vorgeschlagen bzw. das Datum schon
   * bearbeitet — ein bewusst geleertes Datum wird beim erneuten Öffnen des
   * Schritts nicht wieder gefüllt. Beim Säen `false`.
   */
  meldedatumVorgeschlagen: boolean;
  /**
   * Kontakt-Id, die der Server hat bzw. zuletzt geschickt bekam (`null` vor
   * der Anlage). Nach „Kontakt ändern“ ist `kontakt` lokal `null` — ob ein
   * gewählter Kontakt neu ist, entscheidet der Vergleich mit diesem Wert.
   */
  kontaktServer: number | null;
  /**
   * Nur direkt nach der Anlage: der gePOSTete Stand. Er liegt schon auf dem
   * Server; Eingaben während der Anlage weichen davon ab und speichert der
   * Autosave. Jedes Säen (Server/Neuanlage) verwirft ihn.
   */
  basis?: AutosaveBasis | null;
}

const verweis = (id: number | null): Verweis | null => (id === null ? null : { id, label: "" });

/** Frischer Stand der Neuanlage, vorbelegt aus `?contactId=…&riskObjectId=…&contractId=…`. */
function bearbeitungFuerNeu(searchParams: URLSearchParams): Bearbeitung {
  const vorauswahl: Vorauswahl = {
    contactId: zahlOderNull(searchParams.get("contactId")),
    riskObjectId: zahlOderNull(searchParams.get("riskObjectId")),
    contractId: zahlOderNull(searchParams.get("contractId")),
  };
  return {
    damageId: null,
    answers: initialBigAnswers,
    schadensart: null,
    kontakt: verweis(vorauswahl.contactId),
    risiko: verweis(vorauswahl.riskObjectId),
    vertrag: verweis(vorauswahl.contractId),
    abschluss: ABSCHLUSS_LEER,
    meldedatumVorgeschlagen: false,
    kontaktServer: null,
  };
}

function bearbeitungAusSchaden(damage: MappeDamage): Bearbeitung {
  return {
    damageId: damage.id,
    ...antwortenAusMeldung(damage.meldung),
    kontakt: damage.contact ? { id: damage.contact.id, label: damage.contact.displayName ?? "" } : null,
    risiko: damage.riskObject ? { id: damage.riskObject.id, label: damage.riskObject.name ?? "" } : null,
    vertrag: damage.contract ? { id: damage.contract.id, label: damage.contract.contractNbr ?? "" } : null,
    abschluss: abschlussAusSchaden(damage),
    meldedatumVorgeschlagen: false,
    kontaktServer: damage.contact?.id ?? null,
  };
}

/** Geladener Stand, an die Id gebunden, für die er geholt wurde. */
interface Stand {
  damageId: number;
  data: SchadenmappeData | null;
  fehler: string | null;
}

interface ViewToken {
  damageId: number;
  generation: number;
}

export default function SchadenmappePage({ neu = false }: { neu?: boolean }) {
  const params = useParams<{ id?: string; bereich?: string; schritt?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const telefon = useResponsive().width <= TELEFON_BREITE;

  const damageId = neu ? NaN : Number(params.id);
  const idGueltig = Number.isInteger(damageId) && damageId > 0;

  // Jede asynchrone Antwort gehört zu genau einer Ansicht (Id + Generation):
  // ein Wechsel von Schaden A zu B darf B nicht mit A-Daten überschreiben.
  const viewRef = React.useRef<ViewToken>({ damageId, generation: 0 });
  if (!Object.is(viewRef.current.damageId, damageId)) {
    viewRef.current = { damageId, generation: viewRef.current.generation + 1 };
  }
  const isCurrentView = React.useCallback(
    (view: ViewToken) =>
      Object.is(viewRef.current.damageId, view.damageId) &&
      viewRef.current.generation === view.generation,
    [],
  );

  const [stand, setStand] = React.useState<Stand | null>(null);
  const [ladeZaehler, setLadeZaehler] = React.useState(0);

  // Neuanlage: Kontakt/Risiko/Vertrag aus `?contactId=…&riskObjectId=…&contractId=…`
  // vorbelegen; die Namen lädt die Meldung (`SchadenartIntern`) nach.
  const [bearbeitung, setBearbeitung] = React.useState<Bearbeitung>(() =>
    neu
      ? bearbeitungFuerNeu(searchParams)
      : {
          damageId: null,
          answers: initialBigAnswers,
          schadensart: null,
          kontakt: null,
          risiko: null,
          vertrag: null,
          abschluss: ABSCHLUSS_LEER,
          meldedatumVorgeschlagen: false,
          kontaktServer: null,
        },
  );

  // Neuanlage-Sitzung = Navigation (`location.key`) nach `/schaeden/neu`. Die
  // Seite bleibt beim Wechsel `/schaeden/:id` ↔ `/schaeden/neu` dieselbe Instanz;
  // jedes Betreten der Neuanlage beginnt deshalb frisch (React-Muster „State
  // beim Rendern anpassen“). Verglichen wird mit der vorigen Sitzung, nicht mit
  // `bearbeitung.damageId`: Direkt nach der Anlage ist die Id schon gebunden,
  // während `neu` evtl. noch einen Render lang gilt — das ist kein Neubetreten.
  const neuSitzung = neu ? location.key : null;
  const [letzteNeuSitzung, setLetzteNeuSitzung] = React.useState<string | null>(neuSitzung);
  // Vor der Anlage gibt es keine Id und keinen Schritt in der URL: „Kontakt“ und
  // „Schadenart“ wechseln lokal. Mit vorgewähltem Kontakt beginnt die Neuanlage
  // gleich bei der Schadenart.
  const [neuSchritt, setNeuSchritt] = React.useState<"kontakt" | "type">(() =>
    bearbeitung.kontakt ? "type" : "kontakt",
  );
  if (neuSitzung !== letzteNeuSitzung) {
    setLetzteNeuSitzung(neuSitzung);
    if (neuSitzung !== null) {
      const frisch = bearbeitungFuerNeu(searchParams);
      setBearbeitung(frisch);
      setNeuSchritt(frisch.kontakt ? "type" : "kontakt");
    }
  }

  React.useEffect(() => {
    if (neu || !idGueltig) return;
    const view = viewRef.current;
    getSchadenmappe(damageId).then(
      (data) => {
        if (isCurrentView(view)) setStand({ damageId, data, fehler: null });
      },
      (e: unknown) => {
        if (isCurrentView(view)) {
          setStand({ damageId, data: null, fehler: e instanceof Error ? e.message : String(e) });
        }
      },
    );
  }, [neu, idGueltig, damageId, isCurrentView, ladeZaehler]);

  // Stilles Nachladen (Regulierung): nur die Daten austauschen. Scheitert es,
  // bleibt der bisherige Stand stehen.
  const stillNachladen = React.useCallback(async () => {
    if (neu || !idGueltig) return;
    const view = viewRef.current;
    try {
      const neueDaten = await getSchadenmappe(damageId);
      if (isCurrentView(view)) setStand({ damageId, data: neueDaten, fehler: null });
    } catch {
      /* alter Stand bleibt */
    }
  }, [neu, idGueltig, damageId, isCurrentView]);

  // Nur der Stand, der zur aktuellen Id gehört — nie die Daten des vorigen Schadens.
  const aktuell = stand && Object.is(stand.damageId, damageId) ? stand : null;
  const data = aktuell?.data ?? null;
  const damage = data?.damage ?? null;
  const geladen = neu || data !== null;

  // Säen, sobald der Schaden einer anderen Id geladen ist (React-Muster „State
  // beim Rendern anpassen“). Nach der Anlage ist der Stand schon an die neue Id
  // gebunden — das folgende Laden überschreibt die Eingaben daher nicht.
  if (!neu && aktuell && damage && bearbeitung.damageId !== aktuell.damageId) {
    setBearbeitung(bearbeitungAusSchaden(damage));
  }
  // Bis der neue Schaden gesät ist, nicht die Antworten des vorigen zeigen.
  const gebunden = neu || Object.is(bearbeitung.damageId, damageId);
  const answers = gebunden ? bearbeitung.answers : initialBigAnswers;
  const schadensart = gebunden ? bearbeitung.schadensart : null;

  // Setter für die Meldung.
  const update = React.useCallback(
    (patch: Partial<BigAnswers>) => setBearbeitung((b) => ({ ...b, answers: { ...b.answers, ...patch } })),
    [],
  );
  const setSchadensart = React.useCallback(
    (wert: string | null) => setBearbeitung((b) => ({ ...b, schadensart: wert })),
    [],
  );

  // Kontakt/Risiko/Vertrag gehen nach der Anlage sofort an den Server (nicht
  // über den Meldungs-Autosave). Die Antwort aktualisiert den geladenen Schaden
  // derselben Id; `bearbeitung` bleibt (sie ist schon an diese Id gebunden).
  const sofort = useSofortSpeichern({
    damageId: gebunden ? bearbeitung.damageId : null,
    speichern: updateDamage,
    onGespeichert: (gespeichert) =>
      setStand((s) =>
        s && s.data && s.damageId === gespeichert.id
          ? { ...s, data: { ...s.data, damage: { ...s.data.damage, ...gespeichert } } }
          : s,
      ),
  });

  // Lokal immer setzen; gespeichert wird nur bei geänderter Id — ein
  // nachgeladener Name (gleiche Id) ist keine Änderung. Ein geleerter Kontakt
  // („Kontakt ändern“) bleibt lokal, bis einer gewählt ist; verglichen wird
  // dann mit dem Kontakt des Servers (`kontaktServer`), nicht mit dem lokal
  // geleerten — derselbe Kontakt erneut gewählt schickt nichts.
  //
  // PUT-Reihenfolge bei einem anderen Kontakt: `SchadenartIntern` ruft erst
  // `onRisiko(null)`/`onVertrag(null)`, dann `onKontakt` — `sofort` reiht die
  // PUTs in dieser Folge (`riskObject`/`contract` vor `contact`).
  const verweisGeaendert = (alt: number | null, wert: Verweis | null) =>
    bearbeitung.damageId !== null && gebunden && alt !== (wert?.id ?? null);
  const setKontakt = (wert: Verweis | null) => {
    const senden = wert !== null && verweisGeaendert(bearbeitung.kontaktServer, wert);
    // `kontaktServer` wird optimistisch schon vor dem Erfolg des PUTs gesetzt.
    // Scheitert er, bleibt der Patch in `sofort` offen (Kopfzeile „Erneut
    // versuchen“); wählt man den vorigen Kontakt zurück, unterscheidet er sich
    // vom (optimistischen) Stand — es geht korrekt ein PUT mit ihm raus.
    setBearbeitung((b) => ({ ...b, kontakt: wert, ...(senden && wert ? { kontaktServer: wert.id } : {}) }));
    if (senden && wert) void sofort.senden({ contact: { id: wert.id } });
  };
  const setRisiko = (wert: Verweis | null) => {
    setBearbeitung((b) => ({ ...b, risiko: wert }));
    if (verweisGeaendert(bearbeitung.risiko?.id ?? null, wert)) {
      void sofort.senden({ riskObject: wert ? { id: wert.id } : null });
    }
  };
  const setVertrag = (wert: Verweis | null) => {
    setBearbeitung((b) => ({ ...b, vertrag: wert }));
    if (verweisGeaendert(bearbeitung.vertrag?.id ?? null, wert)) {
      void sofort.senden({ contract: wert ? { id: wert.id } : null });
    }
  };

  // Abschluss (Meldung an den Versicherer): lokal immer setzen, sofort
  // speichern nur, was sich gegenüber dem Stand ändert — Kontakte nach Id (ein
  // nachgeladener Name geht nie raus), sonst nach Wert (ein zweiter Klick auf
  // denselben Meldeweg auch nicht). Nur nach der Anlage, für den gebundenen Schaden.
  const setAbschluss = (patch: Partial<AbschlussWerte>) => {
    if (bearbeitung.damageId === null || !gebunden) return;
    const geaendert = abschlussGeaendert(bearbeitung.abschluss, patch);
    setBearbeitung((b) => ({
      ...b,
      abschluss: { ...b.abschluss, ...patch },
      ...("reportDate" in patch ? { meldedatumVorgeschlagen: true } : {}),
    }));
    if (Object.keys(geaendert).length > 0) void sofort.senden(abschlussPatchFuerServer(geaendert));
  };

  // Anlage: einmal, sobald Kontakt und Schadenart feststehen; danach wird die
  // URL ersetzt und die Mappe des neuen Schadens normal geladen.
  const anlage = useSchadenAnlage({
    aktiv: neu && bearbeitung.damageId === null,
    kontaktId: bearbeitung.kontakt?.id ?? null,
    risikoId: bearbeitung.risiko?.id ?? null,
    vertragId: bearbeitung.vertrag?.id ?? null,
    answers,
    schadensart,
    description: () => buildDescription(answers),
    anlegen: createDamage,
    schluessel: neuSitzung,
    onAngelegt: (created, gesendet) => {
      setBearbeitung((b) => ({
        ...b,
        damageId: created.id,
        kontaktServer: created.contact?.id ?? b.kontakt?.id ?? null,
        basis: { damageId: created.id, ...gesendet },
      }));
      // Auf „Schadenart“ bleiben: dort folgt noch die konkrete Schadenart, dann „Weiter“.
      navigate(`/schaeden/${created.id}/meldung/type`, { replace: true });
    },
  });

  // Autosave erst nach der Anlage — und nur für den Schaden, an den der Stand gebunden ist.
  const autosave = useMeldungAutosave({
    damageId: !neu && gebunden ? damageId : null,
    answers,
    schadensart,
    description: () => buildDescription(answers),
    speichern: updateDamage,
    basis: bearbeitung.basis ?? null,
  });

  // Vorrang in der Kopfzeile: Anlage > Sofort-Speichern > Autosave.
  let kopfSpeichern: Speicherzustand = autosave.zustand.status;
  let kopfFehler: string | null = autosave.zustand.fehler;
  let kopfWiederholen: () => void = autosave.wiederholen;
  const sofortStatus = sofort.zustand.status;
  if (
    sofortStatus === "laeuft" ||
    sofortStatus === "fehler" ||
    (sofortStatus === "ok" && autosave.zustand.status === "idle")
  ) {
    kopfSpeichern = sofortStatus;
    kopfFehler = sofort.zustand.fehler;
    kopfWiederholen = sofort.wiederholen;
  }
  if (neu && anlage.zustand === "laeuft") {
    kopfSpeichern = "anlage";
  } else if (neu && anlage.zustand === "fehler") {
    kopfSpeichern = "fehler";
    kopfFehler = anlage.fehler;
    kopfWiederholen = anlage.wiederholen;
  }

  // Aus dem lokalen Stand: ein gesetztes Meldedatum erfüllt „Abschluss“ sofort,
  // nicht erst nach der Antwort des Servers.
  const meldedatum = gebunden ? bearbeitung.abschluss.reportDate : null;

  // Der Kontakt auf dem Server (bzw. zuletzt geschickt) mit Namen — Bezug für
  // „anderer Kontakt“ und „Abbrechen“ in `SchadenartIntern`. Vor der Anlage `null`.
  const kontaktServer = gebunden && bearbeitung.damageId !== null ? bearbeitung.kontaktServer : null;
  const gespeicherterKontakt: Verweis | null =
    kontaktServer === null
      ? null
      : {
          id: kontaktServer,
          label:
            (damage?.contact?.id === kontaktServer ? damage.contact.displayName : null) ??
            (bearbeitung.kontakt?.id === kontaktServer ? bearbeitung.kontakt.label : null) ??
            "",
        };

  // Für den Schritt „Kontakt“: lokal gewählt oder (nach „Kontakt ändern“) noch der des Servers.
  const kontaktGewaehlt = gebunden && (bearbeitung.kontakt !== null || bearbeitung.kontaktServer !== null);

  const bereich: Bereich = !neu && istBereich(params.bereich) ? params.bereich : "meldung";
  const schritt: BigStepKey | null = React.useMemo(() => {
    if (bereich !== "meldung") return null;
    if (neu) return kontaktGewaehlt ? neuSchritt : "kontakt";
    const liste = schrittListeIntern(answers);
    const gewuenscht = params.schritt as BigStepKey | undefined;
    if (gewuenscht && liste.includes(gewuenscht)) return gewuenscht;
    return startSchritt(answers, { meldedatum });
  }, [bereich, neu, neuSchritt, kontaktGewaehlt, answers, params.schritt, meldedatum]);

  // Besuchte Schritte: je Schaden neu, jeder angezeigte Schritt kommt dazu.
  const [besucht, setBesucht] = React.useState<{ damageId: number; keys: Set<BigStepKey> }>(
    () => ({ damageId, keys: new Set() }),
  );
  const besuchtKeys = Object.is(besucht.damageId, damageId) ? besucht.keys : null;
  React.useEffect(() => {
    if (!geladen || schritt === null) return;
    setBesucht((alt) => {
      const gleich = Object.is(alt.damageId, damageId);
      if (gleich && alt.keys.has(schritt)) return alt;
      const keys = new Set(gleich ? alt.keys : []);
      keys.add(schritt);
      return { damageId, keys };
    });
  }, [geladen, schritt, damageId]);

  const eintraege = React.useMemo(
    () =>
      schrittEintraege(answers, schritt ?? "done", besuchtKeys ?? KEINE_BESUCHT, {
        art: "intern",
        meldedatum,
        kontakt: kontaktGewaehlt,
      }),
    [answers, schritt, besuchtKeys, meldedatum, kontaktGewaehlt],
  );

  // Für „Prüfen“: alle Schritte mit unerfüllter Pflicht — besucht oder nicht.
  const fehlende = React.useMemo<SchrittEintrag[]>(
    () =>
      schrittListeIntern(answers)
        .filter(
          (k) =>
            k !== "summary" &&
            !pflichtErfuellt(k, answers, { art: "intern", meldedatum, kontakt: kontaktGewaehlt }),
        )
        .map((key) => ({ key, label: schrittLabel(key), zustand: "fehlt" })),
    [answers, meldedatum, kontaktGewaehlt],
  );

  const onWechsel = React.useCallback(
    (ziel: Bereich, zielSchritt?: BigStepKey) => {
      if (neu) {
        // Vor der Anlage gibt es keine Id: nur „Kontakt“ ↔ „Schadenart“, lokal.
        if (ziel === "meldung" && (zielSchritt === "kontakt" || (zielSchritt === "type" && kontaktGewaehlt))) {
          setNeuSchritt(zielSchritt);
        }
        return;
      }
      void autosave.jetzt();
      navigate(
        ziel === "meldung" && zielSchritt
          ? `/schaeden/${damageId}/meldung/${zielSchritt}`
          : `/schaeden/${damageId}/${ziel}`,
      );
    },
    [neu, kontaktGewaehlt, navigate, damageId, autosave.jetzt],
  );

  let inhalt: React.ReactNode;
  if (!neu && !idGueltig) {
    inhalt = <div className="smp-meldung-fehler" role="alert">Ungültige Schadennummer.</div>;
  } else if (aktuell?.fehler) {
    inhalt = (
      <div className="smp-meldung-fehler" role="alert">
        Der Schaden konnte nicht geladen werden ({aktuell.fehler}).{" "}
        <button
          type="button"
          className="smp-kopf-fehler"
          onClick={() => {
            setStand(null); // „Lädt…“ statt des alten Fehlers
            setLadeZaehler((n) => n + 1);
          }}
        >
          Erneut laden
        </button>
      </div>
    );
  } else if (!geladen) {
    inhalt = <div className="smp-platzhalter" aria-busy="true">Lädt…</div>;
  } else if (bereich === "meldung" && schritt !== null) {
    inhalt = (
      <MeldungBereich
        answers={answers}
        update={update}
        schadensart={schadensart}
        onSchadensart={setSchadensart}
        kontakt={gebunden ? bearbeitung.kontakt : null}
        onKontakt={setKontakt}
        risiko={gebunden ? bearbeitung.risiko : null}
        onRisiko={setRisiko}
        vertrag={gebunden ? bearbeitung.vertrag : null}
        onVertrag={setVertrag}
        gespeicherterKontakt={gespeicherterKontakt}
        schritt={schritt}
        onSchritt={(s) => onWechsel("meldung", s)}
        abschluss={gebunden ? bearbeitung.abschluss : ABSCHLUSS_LEER}
        onAbschluss={setAbschluss}
        meldedatumVorschlagen={gebunden && !bearbeitung.meldedatumVorgeschlagen}
        fehlende={fehlende}
        angelegt={bearbeitung.damageId !== null && gebunden}
        anlageLaeuft={anlage.zustand === "laeuft"}
        damageId={gebunden ? bearbeitung.damageId : null}
        vorgangId={damage?.vorgangId ?? null}
        // Bei geleertem Kontakt („Kontakt ändern“) gilt für die Unterlagen der des Servers.
        contactId={gebunden ? bearbeitung.kontakt?.id ?? bearbeitung.kontaktServer : null}
      />
    );
  } else if (bereich === "bearbeitung" && data) {
    inhalt = (
      <BearbeitungBereich
        data={data}
        answers={answers}
        onAntworten={update}
        abschluss={gebunden ? bearbeitung.abschluss : ABSCHLUSS_LEER}
        onAbschluss={setAbschluss}
        onPatch={sofort.senden}
      />
    );
  } else if (data) {
    // Stilles Nachladen: `stand` bleibt stehen, bis die neuen Daten da sind —
    // kein Ladezustand, kein Remount der Karten.
    inhalt = <RegulierungBereich data={data} onReload={stillNachladen} />;
  } else {
    inhalt = <div className="smp-platzhalter" aria-busy="true">Lädt…</div>;
  }

  return (
    <div className="kvp-rd smp-flaeche">
      {kopfSpeichern === "fehler" && (
        <div className="smp-speicherfehler" role="alert">
          Speichern fehlgeschlagen{kopfFehler ? ` (${kopfFehler})` : ""}.{" "}
          <button type="button" className="smp-kopf-fehler" onClick={kopfWiederholen}>Erneut versuchen</button>
        </div>
      )}
      <div className="smp-body">
        <Seitenleiste
          schritte={eintraege}
          bereich={bereich}
          schritt={schritt}
          gesperrt={neu || !idGueltig || anlage.zustand === "laeuft"}
          onWechsel={onWechsel}
          telefon={telefon}
        />
        <main className="smp-inhalt">{inhalt}</main>
      </div>
    </div>
  );
}
